import { action, observable, makeObservable, runInAction, reaction } from 'mobx';
import RootStore from '../RootStore';
import epaxios from '@/utils/epaxios';
import { captureException } from '@sentry/browser';
import * as constants from './constants';
import {
  extractUniqueCustomers,
  updateCustomerInformation,
  updateRoomConfiguration,
  updateSiteConfiguration,
} from './SiteSurveyStore.utils';
import { convertCustomerInformationStepDataToDTO } from './adapters/customerInformationStepAdapter';
import { prepareSiteConfigurationStepPayload } from './adapters/siteConfigurationStepAdapter';
import { prepareRoomConfigurationStepPayload } from './adapters/roomConfigurationStepAdapter';
import { notification } from 'antd';
import { t } from 'i18next';
import { permissions } from '@/constants';
import { AuthenticationType, DryerFormType, TankFormType } from './constants';
import { keyBy } from 'lodash';

export default class SiteSurveyStore {
  rootStore: typeof RootStore;

  @observable customerInformationData: constants.CustomerInformationStepData = {
    ...constants.DEFAULT_CUSTOMER_INFORMATION_STEP_VALUES,
  };

  @observable siteConfigurationData: constants.SiteConfigurationStepData = {
    ...constants.DEFAULT_SITE_CONFIGURATION_STEP_VALUES,
  };

  @observable roomConfigurationStepData: constants.RoomConfigurationStepData = {
    ...constants.DEFAULT_ROOM_CONFIGURATION_STEP_VALUES,
  };

  @observable tankForms: Record<number, TankFormType> = {};

  @observable dryerForms: Record<number, DryerFormType> = {};

  @observable existingFormsBySalesforceAccount: constants.OnboardingFormPayload[] = [];

  @observable isLoading = false;
  @observable isUserHasInfoCollectionPermission = false;
  @observable isUserEligibleToForm = false;
  @observable errors: constants.StoreErrors = {};
  @observable accountData: constants.AccountResponse | null = null;
  @observable userData: constants.UserData[] = [];
  @observable modelSpecs: constants.ModelSpec[] = [];
  @observable formId: string | null = null;
  @observable expandedSections: Record<number, boolean> = {};
  @observable validationState: Record<string, boolean> = {};
  @observable pollingIntervalId: number | null = null;
  @observable stationId: string | null = null;
  @observable currentStep = 0;
  @observable manufacturersList: { label: string; value: string }[] = [];
  @observable selectedRegion: string | null = null;

  constructor(rootStore: typeof RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    reaction(
      () => this.rootStore.userStore.stations,
      () => {
        for (const station of this.rootStore.userStore.stations) {
          for (const permission of station.permissions) {
            if (permission.name === permissions.COLLECT_INFO_AND_CREATE_STATION) {
              this.isUserHasInfoCollectionPermission = true;
              this.isUserEligibleToForm = this.rootStore.userStore.getAuthenticationType === AuthenticationType.SSO;
              return;
            }
          }
        }
      },
    );
  }

  @action
  setSectionExpanded(section: number, expanded: boolean): void {
    this.expandedSections[section] = expanded;
  }

  @action
  updateValidation(key: string, isValid: boolean): void {
    this.validationState[key] = isValid;
  }

  @action
  setFormId(id: string): void {
    this.formId = id;
  }

  @action
  updateCustomerInformationData(data: Partial<constants.CustomerInformationStepData>): void {
    this.customerInformationData = { ...this.customerInformationData, ...data };
  }

  @action
  updateSiteConfigurationData(data: Partial<constants.SiteConfigurationStepData>): void {
    if (!data?.pressureType && !this.siteConfigurationData?.pressureType)
      data.pressureType = constants.PressureTypeFull.MAIN;
    this.siteConfigurationData = { ...this.siteConfigurationData, ...data };
  }

  @action
  updateRoomConfigurationStepData(data: Partial<constants.RoomConfigurationStepData>): void {
    const cleanData = JSON.parse(JSON.stringify(data));
    this.roomConfigurationStepData = { ...this.roomConfigurationStepData, ...cleanData };
  }

  @action
  async updateStateByExistingForm(form: constants.OnboardingFormPayload): Promise<void> {
    const salesforceData = await this.fetchAccountData(form?.salesforceAccountId);
    this.formId = form?.formId;

    updateCustomerInformation(form, salesforceData, this.customerInformationData);
    updateSiteConfiguration(form, this.siteConfigurationData);
    const { newRoomConfigurationStepData, newTankForms, newDryerForms } = updateRoomConfiguration(
      form,
      this.roomConfigurationStepData,
      this.siteConfigurationData,
      this.tankForms,
      this.dryerForms,
    );
    this.roomConfigurationStepData = newRoomConfigurationStepData;
    this.tankForms = newTankForms;
    this.dryerForms = newDryerForms;
  }

  @action
  resetForm(): void {
    this.customerInformationData = { ...constants.DEFAULT_CUSTOMER_INFORMATION_STEP_VALUES };
    this.siteConfigurationData = { ...constants.DEFAULT_SITE_CONFIGURATION_STEP_VALUES };
    this.roomConfigurationStepData = { ...constants.DEFAULT_ROOM_CONFIGURATION_STEP_VALUES };
    this.tankForms = {};
    this.dryerForms = {};
    this.errors = {};
    this.formId = null;
    this.expandedSections = {};
    this.validationState = {};
    this.currentStep = 0;
    this.stationId = null;
    this.existingFormsBySalesforceAccount = [];
  }

  @action
  async fetchAccountData(accountId: string): Promise<constants.AccountResponse> {
    try {
      this.setLoading(true);
      const response = await epaxios.get<constants.AccountResponse>(
        `salesforce/site-onboarding-form/salesforce-account/${accountId}/region/${this.selectedRegion}`,
      );

      const data = response.data;

      runInAction(() => {
        this.accountData = data;

        this.customerInformationData.customer = {
          name: data.customerName,
          accountId: data.accountId,
          cgOrgChannelC: data.cgOrgChannelC,
        };

        this.customerInformationData.siteAddress = {
          streetAddress: data.siteAddress.street,
          city: data.siteAddress.city,
          state: data.siteAddress.state,
          country: data.siteAddress.country,
          zipCode: data.siteAddress.postalCode,
        };

        this.customerInformationData.accountManager = {
          fullName: data.accountOwnerC,
          email: '',
        };

        this.roomConfigurationStepData.assets = data.assets || [];
        this.customerInformationData.newCustomerName = data.customerName;

        this.setLoading(false);
      });

      return data;
    } catch (error) {
      runInAction(() => {
        this.setLoading(false);
        this.errors = { fetch: error instanceof Error ? error.message : 'An unknown error occurred' };
      });
      throw error;
    }
  }

  @action
  async fetchModelSpecs(): Promise<constants.ModelSpec[]> {
    try {
      this.setLoading(true);

      const response = await epaxios.get('station-mgmt/device-model/repo');
      const deviceModels = response.data;

      runInAction(() => {
        this.modelSpecs = deviceModels
          .filter(
            (model: { deviceControllerId?: string; technicalName?: string }) =>
              model.deviceControllerId && model.technicalName,
          )
          .map(
            (model: {
              id: string;
              technicalName?: string;
              attributes?: {
                modelDisplayName?: string;
                brand?: string;
              };
              deviceControllerId?: string;
            }) => ({
              modelSpecName: model.technicalName || '',
              modelDisplayName: model.technicalName || '',
              modelId: model.id,
              deviceControllerId: model.deviceControllerId || '',
              deviceControllerName: model.attributes?.brand || '',
              id: model.id,
              technicalName: model.technicalName || '',
            }),
          );

        this.setLoading(false);
      });

      return this.modelSpecs;
    } catch (error) {
      runInAction(() => {
        this.setLoading(false);
        this.errors = { fetch: error instanceof Error ? error.message : 'An unknown error occurred' };
      });
      throw error;
    }
  }

  @action
  setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  @action
  getFormId(): string | null {
    return this.formId;
  }

  @action
  async fetchUserData(): Promise<void> {
    try {
      if (
        this.rootStore.userStore &&
        this.rootStore.userStore.stations &&
        this.rootStore.userStore.stations.length > 0
      ) {
        runInAction(() => {
          this.userData = extractUniqueCustomers(this.rootStore.userStore.stations);
        });

        return;
      }

      this.setLoading(true);

      const response = await epaxios.get('user/me');
      const data = response.data;

      runInAction(() => {
        this.userData = extractUniqueCustomers(data.stations);
        this.setLoading(false);
      });
    } catch (error) {
      runInAction(() => {
        this.setLoading(false);
        this.errors = { fetch: error instanceof Error ? error.message : 'An unknown error occurred' };
      });
      throw error;
    }
  }

  @action
  private async saveFormData(
    { images, ...payload }: constants.SaveOnboardingFormPayload,
    shouldSaveImages?: boolean,
  ): Promise<{ data: { formId: string } }> {
    try {
      this.setLoading(true);

      const response = await epaxios.post<{ formId: string }>('info-collection/onboarding-forms', payload);

      if (shouldSaveImages) {
        await this.saveFormImages(this.formId, images);
      }

      this.setLoading(false);
      return { data: response.data };
    } catch (error) {
      runInAction(() => {
        this.setLoading(false);
        this.errors = {
          save: error instanceof Error ? error.message : 'An unknown error occurred while saving data',
        };
      });
      throw error;
    }
  }

  private async saveFormImages(formId: string, images: constants.CreateImagePayload[]): Promise<void> {
    await epaxios.delete(`info-collection/onboarding-forms/${formId}/image`);

    if (!images?.length) {
      return;
    }

    const signedPutUrls = await epaxios.post<{ id: string; url: string }[]>(
      `info-collection/onboarding-forms/${formId}/image`,
      {
        images: images.map(({ file, ...image }) => ({ ...image, originalFilename: file.name })),
      },
    );

    const imagesDict = keyBy(images, 'id');

    const uploadRequests = signedPutUrls.data.map(image =>
      fetch(image.url, {
        method: 'PUT',
        body: imagesDict[image.id].file,
        headers: {
          'Content-Type': imagesDict[image.id].file.type,
        },
      }),
    );

    try {
      await Promise.all(uploadRequests);
    } catch (error) {
      captureException(error);
      throw error;
    }
  }

  @action
  async saveCustomerInformationStepData(accountId: string): Promise<{ data: { formId: string } }> {
    if (!accountId) {
      throw new Error('Salesforce Account ID is required but was not provided');
    }

    const payload = convertCustomerInformationStepDataToDTO(
      this.customerInformationData,
      this.accountData,
      this.formId,
      this.selectedRegion,
    );
    payload.salesforceAccountId = accountId;

    const response = await this.saveFormData(payload);

    if (response?.data?.formId) {
      runInAction(() => {
        this.formId = response.data.formId;
      });
    }

    return response;
  }

  @action
  async saveSiteConfigurationStepData(): Promise<{ data: { formId: string } }> {
    const payload = prepareSiteConfigurationStepPayload(
      this.customerInformationData,
      this.siteConfigurationData,
      this.accountData,
      this.formId,
    );
    return this.saveFormData(payload);
  }

  @action
  async saveRoomConfigurationStepData(
    assetsFormData: Partial<constants.RoomConfigurationStepData>,
  ): Promise<{ data: { formId: string } }> {
    const roomConfigurationStepDataToUse =
      assetsFormData && Object.keys(assetsFormData).length > 0 ? assetsFormData : this.roomConfigurationStepData;
    const payload = await prepareRoomConfigurationStepPayload(
      this.customerInformationData,
      this.siteConfigurationData,
      roomConfigurationStepDataToUse,
      this.accountData,
      this.formId,
    );
    return this.saveFormData(payload, true);
  }

  @action
  async submitOnboardingForm(): Promise<Record<string, unknown>> {
    try {
      this.setLoading(true);

      if (!this.formId) {
        throw new Error('No form ID available. Cannot submit without a form ID.');
      }

      const response = await epaxios.post(`info-collection/onboarding-forms/${this.formId}/submit`);
      this.setLoading(false);
      return response.data;
    } catch (error) {
      runInAction(() => {
        this.setLoading(false);
        this.errors = {
          submitForm: error instanceof Error ? error.message : 'An unknown error occurred while submitting the form',
        };
      });
      throw error;
    }
  }

  @action
  startPollingFormStatus() {
    if (this.pollingIntervalId !== null) {
      this.stopPollingFormStatus();
    }

    const formId = this.getFormId();
    if (!formId) {
      notification.error({
        message: t('Error'),
        description: t('No form ID available. Please try again.'),
        placement: 'bottomLeft',
      });
      return;
    }

    const pollInterval = 5000;
    const maxPolls = 6;
    let pollCount = 0;

    this.checkFormStatus(formId, pollCount, maxPolls);

    this.pollingIntervalId = window.setInterval(() => {
      pollCount++;
      this.checkFormStatus(formId, pollCount, maxPolls);
    }, pollInterval);
  }

  @action
  stopPollingFormStatus() {
    if (this.pollingIntervalId !== null) {
      window.clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }
  }

  @action
  async deleteForm(formId: string): Promise<void> {
    this.setLoading(true);
    await epaxios.delete(`info-collection/onboarding-forms/${formId}`);
    this.resetForm();
    this.setLoading(false);
  }

  @action
  async fetchManyFormBySalesforceAccountForContinue(
    salesforceAccountId: string,
  ): Promise<constants.OnboardingFormPayload[]> {
    this.setLoading(true);
    const response = await epaxios.get(
      `info-collection/onboarding-forms/salesforce-account/${salesforceAccountId}?statuses=design,fail`,
    );
    this.setLoading(false);
    this.existingFormsBySalesforceAccount = response.data;
    return response.data;
  }

  @action
  async checkFormStatus(formId: string, pollCount: number, maxPolls: number) {
    try {
      this.setLoading(true);

      const response = await epaxios.get<constants.FormStatusResponse>(`info-collection/onboarding-forms/${formId}`);
      const { status, createdEntitiesData, imageUploadSummary } = response.data;

      runInAction(() => {
        if (status === constants.FormStatus.COMPLETE && createdEntitiesData.stationId) {
          this.stationId = createdEntitiesData.stationId;
          notification.success({
            message: t('Success'),
            description: t('Station created successfully'),
            placement: 'bottomLeft',
          });

          if (imageUploadSummary?.failedUploads.length) {
            notification.error({
              message: t('imageUploadFailedMessage'),
              description: t('imageUploadFailedMessageDescription', {
                failedCount: imageUploadSummary.failedUploads.length,
                totalCount: imageUploadSummary.totalImages,
              }),
              placement: 'bottomLeft',
            });
          }
          this.stopPollingFormStatus();
        } else if (status === constants.FormStatus.FAIL) {
          notification.error({
            message: t('Error'),
            description: t('Failed to create station'),
            placement: 'bottomLeft',
          });
          this.stopPollingFormStatus();
        } else if (pollCount >= maxPolls) {
          notification.error({
            message: t('Timeout'),
            description: t('Station creation timed out after 30 seconds'),
            placement: 'bottomLeft',
          });
          this.stopPollingFormStatus();
        }
        this.setLoading(false);
      });
    } catch (error) {
      runInAction(() => {
        console.error('Error polling form status:', error);

        if (pollCount >= maxPolls) {
          notification.error({
            message: t('Error'),
            description: t('Failed to check station status'),
            placement: 'bottomLeft',
          });
          this.stopPollingFormStatus();
        }

        this.setLoading(false);
        this.errors = {
          pollingStatus: error instanceof Error ? error.message : 'An unknown error occurred',
        };
      });
    }
  }

  @action
  getStationId(): string | null {
    return this.stationId;
  }

  @action
  async fetchManufacturersList(): Promise<{ label: string; value: string }[]> {
    if (this.manufacturersList && this.manufacturersList.length > 0) {
      return this.manufacturersList;
    }
    const response = await epaxios.get('station-mgmt/device-brand');

    this.manufacturersList = response.data.map((brand: string) => ({ label: brand, value: brand }));
    return this.manufacturersList;
  }

  @action
  setCurrentStep(step: number): void {
    this.currentStep = step;
  }

  @action
  setSelectedRegion(region: string | null): void {
    this.selectedRegion = region;
  }
}
